import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    alpha,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Grid,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/pro-solid-svg-icons';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useApplicationContext } from 'ApplicationContext';
import { SettingsSection, settingsSectionPath, visibleSettingsSections } from './settingsSections';
import { settingsApi } from './settingsApi';

/**
 * Zustand eines Bereichs, wie ihn `GET /Settings/Status` liefert.
 *
 * Von Hand deklariert, weil der generierte Client (`src/apiClient/Api.ts`) erst beim nächsten
 * `npm run generate-api` gegen einen laufenden Server aktualisiert wird. Danach kann hier auf
 * die generierten Typen umgestellt werden — die Feldnamen sind identisch.
 */
interface SettingsAreaStatus {
    key: string;
    enabled: boolean;
    configured: boolean;
    healthy: boolean | null;
    detail: string;
    elapsedMilliseconds: number | null;
}

interface SettingsStatus {
    checkedAt: string;
    areas: SettingsAreaStatus[];
}

/** Anzeigeform eines Zustands: Punktfarbe und Beschriftung gehören zusammen. */
type StatusDisplay = {
    /** Theme-Pfad der Punktfarbe, z. B. `success.main`. */
    dotColor: string;
    labelKey: string;
};

/**
 * Übersetzt den Serverzustand in eine Anzeige.
 *
 * Die Reihenfolge der Fälle ist die Rangfolge der Ursachen: unvollständig konfiguriert ist ein
 * anderer Fehler als nicht erreichbar, und beides ist etwas anderes als bewusst abgeschaltet.
 */
function toStatusDisplay(status: SettingsAreaStatus | undefined): StatusDisplay | undefined {
    if (!status) return undefined;
    if (!status.enabled) {
        return { dotColor: 'text.disabled', labelKey: 'settings.status.disabled' };
    }
    if (!status.configured) {
        return { dotColor: 'warning.main', labelKey: 'settings.status.incomplete' };
    }
    if (status.healthy === true) {
        return { dotColor: 'success.main', labelKey: 'settings.status.healthy' };
    }
    if (status.healthy === false) {
        return { dotColor: 'error.main', labelKey: 'settings.status.unreachable' };
    }
    return { dotColor: 'text.disabled', labelKey: 'settings.status.unchecked' };
}

/**
 * Einstiegsseite der Verwaltung: eine Kachel je Bereich, mit Zustand dort, wo der Server einen
 * meldet. Damit sieht man ohne Klick durch alle Unterseiten, wo etwas hakt.
 */
export const SettingsOverview: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const context = useApplicationContext();
    // Über request() statt api.instance — sonst fehlt der Authorization-Header.
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [status, setStatus] = useState<SettingsStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const sections = useMemo(
        () => visibleSettingsSections(context.userData!.userGroup!),
        [context.userData],
    );

    const load = useCallback(
        async (refresh: boolean) => {
            setLoading(true);
            setError(null);
            try {
                const response = await http.get<SettingsStatus>(
                    '/Settings/Status', refresh ? { refresh: true } : undefined);
                setStatus(response.data);
            } catch {
                // Die Übersicht bleibt ohne Zustände benutzbar — die Kacheln verlinken weiterhin.
                setError(t('settings.overview.statusFailed'));
            } finally {
                setLoading(false);
            }
        },
        [http, t],
    );

    useEffect(() => {
        void load(false);
    }, [load]);

    const statusByKey = useMemo(() => {
        const map = new Map<string, SettingsAreaStatus>();
        status?.areas.forEach((area) => map.set(area.key, area));
        return map;
    }, [status]);

    const renderCard = (section: SettingsSection) => {
        const path = settingsSectionPath(section);
        const areaStatus = section.hasStatus ? statusByKey.get(section.key) : undefined;
        const display = toStatusDisplay(areaStatus);

        const content = (
            <CardContent
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}
            >
                <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.9rem',
                            color: 'primary.main',
                            bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.1),
                        }}
                    >
                        <FontAwesomeIcon icon={section.icon} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.925rem', fontWeight: 600 }}>
                        {t(section.labelKey)}
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {t(section.descriptionKey)}
                </Typography>
                {display && (
                    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                flexShrink: 0,
                                bgcolor: display.dotColor,
                            }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 550 }}>
                            {t(display.labelKey)}
                        </Typography>
                        {areaStatus?.detail && (
                            <Typography variant="caption" color="text.disabled" noWrap title={areaStatus.detail}>
                                · {areaStatus.detail}
                            </Typography>
                        )}
                    </Stack>
                )}
                {!path && (
                    <Typography variant="caption" color="text.disabled">
                        {t('settings.status.noPage')}
                    </Typography>
                )}
            </CardContent>
        );

        return (
            <Grid key={section.key} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card
                    variant="outlined"
                    sx={{
                        height: '100%',
                        borderRadius: 2.5,
                        transition: 'border-color 0.12s ease',
                        ...(path
                            ? { '&:hover': { borderColor: 'primary.main' } }
                            : { borderStyle: 'dashed', bgcolor: 'action.hover' }),
                    }}
                >
                    {path ? (
                        <CardActionArea onClick={() => navigate(path)} sx={{ height: '100%' }}>
                            {content}
                        </CardActionArea>
                    ) : (
                        content
                    )}
                </Card>
            </Grid>
        );
    };

    return (
        <Box>
            <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', rowGap: 1 }}
            >
                <Box>
                    <Typography component="h2" variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {t('settings.overview.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {status
                            ? t('settings.overview.checkedAt', {
                                  time: new Date(status.checkedAt).toLocaleTimeString(),
                              })
                            : t('settings.overview.subtitle')}
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    color="inherit"
                    disabled={loading}
                    onClick={() => void load(true)}
                    startIcon={<FontAwesomeIcon icon={faRotate} />}
                    sx={{
                        color: 'text.secondary',
                        borderColor: 'divider',
                        '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'transparent' },
                    }}
                >
                    {t('settings.overview.recheck')}
                </Button>
            </Stack>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={1.75}>
                {sections.map(renderCard)}
            </Grid>
        </Box>
    );
};

export default SettingsOverview;
